import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import { useAuth } from '../../context/AuthContext';
import { bottomTabsFor, navItemForPath } from '../navModel';
import { useChromeConfig } from './ChromeContext';
import { useBrand } from '../../branding/BrandContext';

/**
 * The phone top bar: «رجوع» (or the Mize mark on a tab's home screen), the screen's title,
 * the screen's own actions, and the notifications bell with its unread count.
 */
interface Props {
  unread: number;
  onOpenNotifications: () => void;
}

const parentOf = (path: string) => {
  const parts = path.split('/').filter(Boolean);
  parts.pop();
  return parts.length ? `/${parts.join('/')}` : '/';
};

const hasInAppHistory = () => {
  const st = window.history.state as { idx?: number } | null;
  return !!st && typeof st.idx === 'number' && st.idx > 0;
};

const MobileTopBar: React.FC<Props> = ({ unread, onOpenNotifications }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const brand = useBrand(); // a white-label workshop: its name and mark instead of Mize Pro's
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const cfg = useChromeConfig();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  // A page's own <h1 class="page-title"> moves up into the bar (and is hidden in the page), so
  // the title is never shown twice and dynamic titles («بطاقة عمل 123») still reach the bar.
  const [hoisted, setHoisted] = useState<string | null>(null);
  useEffect(() => {
    setHoisted(null);
    let raf = 0;
    const find = () => {
      const h = document.querySelector<HTMLElement>('.page-container h1.page-title');
      if (!h) return;
      const text = (h.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || text.length > 60) return;
      if (h.getAttribute('data-m-hoisted') !== '1') h.setAttribute('data-m-hoisted', '1');
      setHoisted((cur) => (cur === text ? cur : text));
    };
    find();
    const obs = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(find); });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { obs.disconnect(); cancelAnimationFrame(raf); };
  }, [pathname]);

  const tabRoots = bottomTabsFor(user).map((tab) => tab.to);
  const isRoot = pathname === '/' || tabRoots.includes(pathname);
  const showBack = cfg.backTo !== null && !isRoot;

  const navItem = navItemForPath(user, pathname);
  let fallbackTitle = brand ? (i18n.language === 'ar' ? brand.name.ar : brand.name.en) : 'Mize Pro';
  if (navItem && (navItem.to === pathname || navItem.to === '/')) {
    fallbackTitle = String(t(navItem.labelKey, { defaultValue: navItem.fallback || navItem.labelKey }));
  } else if (/^\/orders\/[^/]+$/.test(pathname)) {
    fallbackTitle = String(t('mobile.order', 'الطلب'));
  } else if (navItem) {
    fallbackTitle = String(t(navItem.labelKey, { defaultValue: navItem.fallback || navItem.labelKey }));
  }
  const title = cfg.title ?? hoisted ?? fallbackTitle;

  const goBack = () => {
    if (hasInAppHistory()) navigate(-1);
    else navigate(cfg.backTo || parentOf(pathname), { replace: true });
  };

  return (
    <header className={`m-topbar${scrolled ? ' is-scrolled' : ''}`}>
      <div className="m-topbar-row">
        {showBack ? (
          <button type="button" className="m-icon-btn m-topbar-back" onClick={goBack} aria-label={t('common.back', 'Back')}>
            <Icon name="chevron" size={24} className="m-back-icon" />
          </button>
        ) : (
          <span className="m-topbar-mark" aria-hidden="true">
            <img src={brand ? brand.mark : '/favicon.png?v=horse1'} alt="" width={28} height={28} />
          </span>
        )}
        <div className="m-topbar-titles">
          <h1 className="m-topbar-title">{title}</h1>
          {cfg.subtitle && <div className="m-topbar-sub">{cfg.subtitle}</div>}
        </div>
        {cfg.actions && <div className="m-topbar-actions">{cfg.actions}</div>}
        <button
          type="button"
          className="m-icon-btn m-topbar-bell"
          onClick={onOpenNotifications}
          aria-label={unread > 0 ? `${t('notifications.title')} (${unread})` : String(t('notifications.title'))}
        >
          <Icon name="bell" size={22} />
          {unread > 0 && <span className="m-badge">{unread > 99 ? '99+' : unread}</span>}
        </button>
      </div>
    </header>
  );
};

export default MobileTopBar;
